<script setup lang="ts">
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSpeechButton,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputProvider,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The full multi-line composer used inside the takeover, built on the AI
 * Elements `PromptInput` primitives (host-resolved via `@/components/ai-elements`
 * — the shared module must not vendor reka-ui/Vue itself).
 *
 * It owns the PromptInput context so it can both submit and, while a reply is
 * streaming, repurpose the submit affordance as a **Stop** control (AI Elements
 * renders a square for `status: "streaming"`), emitting `cancel` instead of
 * `send`.
 */
const props = withDefaults(
  defineProps<{
    /** Agent display name, for the placeholder. */
    agentName?: string | null;
    /** A reply is currently streaming (submit becomes Stop). */
    busy?: boolean;
    /** Fully disable the composer (e.g. while the agent resolves). */
    disabled?: boolean;
    /** Optional hint shown above the input when disabled (e.g. answer the block above). */
    hint?: string | null;
    /** Draft text to pre-fill (carried from the floating input on handoff). */
    initialDraft?: string;
  }>(),
  { agentName: null, busy: false, disabled: false, hint: null, initialDraft: "" },
);

const emit = defineEmits<{
  (e: "send", text: string): void;
  (e: "cancel"): void;
}>();

const { t } = useI18n({ useScope: "local" });

const { textInput, clearInput } = usePromptInputProvider({
  initialInput: props.initialDraft ?? "",
  onSubmit: (message: PromptInputMessage) => onSubmit(message.text),
});

function onSubmit(text: string) {
  if (props.busy) {
    // Square / Stop: abort the in-flight reply, keep any draft intact.
    emit("cancel");
    return;
  }
  const trimmed = text.trim();
  if (!trimmed || props.disabled) {
    return;
  }
  emit("send", trimmed);
  clearInput();
}

const status = computed<"streaming" | undefined>(() => (props.busy ? "streaming" : undefined));
const canSend = computed(() => textInput.value.trim().length > 0);

const placeholder = computed(() => t("placeholder", { agent: props.agentName ?? t("assistant") }));

const rootRef = ref<HTMLElement | null>(null);
function focus() {
  const textarea = rootRef.value?.querySelector("textarea");
  if (!textarea) {
    return;
  }
  textarea.focus();
  // Place the caret at the end so a carried-over draft continues naturally.
  const end = textarea.value.length;
  textarea.setSelectionRange(end, end);
}
defineExpose({ focus });
</script>

<template>
  <div ref="rootRef" class="mx-auto w-full max-w-3xl px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
    <p v-if="hint" class="mb-1.5 px-1 text-xs text-muted-foreground">{{ hint }}</p>
    <PromptInput class="rounded-2xl shadow-sm">
      <PromptInputBody>
        <PromptInputTextarea
          :placeholder="placeholder"
          :aria-label="placeholder"
          :disabled="disabled"
        />
      </PromptInputBody>
      <PromptInputFooter>
        <PromptInputTools class="ml-auto">
          <PromptInputSpeechButton />
          <PromptInputSubmit
            :status="status"
            :disabled="disabled || (!busy && !canSend)"
          />
        </PromptInputTools>
      </PromptInputFooter>
    </PromptInput>
  </div>
</template>

<i18n lang="yaml">
en:
  assistant: the assistant
  placeholder: Message {agent}…
</i18n>
